import React, { useState } from 'react';
import { Send, MapPin, Phone, Mail, MessageCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { mockData } from '../mock';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));

    toast({
      title: "Mensagem enviada com sucesso!",
      description: "Em breve entraremos em contato com você.",
    });

    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefone',
      value: mockData.school.phone,
      link: `tel:${mockData.school.phone}`,
      color: 'from-blue-400 to-indigo-500'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: 'Envie uma mensagem',
      link: `https://wa.me/${mockData.school.whatsapp}`,
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: Mail,
      title: 'E-mail',
      value: mockData.school.email,
      link: `mailto:${mockData.school.email}`,
      color: 'from-red-400 to-pink-500'
    },
    {
      icon: MapPin,
      title: 'Endereço',
      value: mockData.school.address,
      link: mockData.school.mapsLink,
      color: 'from-amber-400 to-orange-500'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-amber-50/50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block mb-4">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-100 px-4 py-2 rounded-full">
              Entre em Contato
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Vamos Conversar?
          </h2>
          <p className="text-lg text-gray-600">
            Agende uma visita ou tire suas dúvidas. Estamos aqui para ajudar!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Fale Conosco</h3>
              <p className="text-gray-600 leading-relaxed">
                Nossa equipe está pronta para atendê-lo. Entre em contato através de qualquer um dos canais abaixo.
              </p>
            </div>

            <div className="grid gap-4">
              <a href={`tel:${mockData.school.phone}`} className="block group">
                <Card className="hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-amber-200">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500 mb-1">Telefone</div>
                      <div className="font-semibold text-gray-800 group-hover:text-amber-600 transition-colors">
                        {mockData.school.phone}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>

              <a href={`https://wa.me/${mockData.school.whatsapp}`} target="_blank" rel="noopener noreferrer" className="block group">
                <Card className="hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-amber-200">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <MessageCircle className="text-white" size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500 mb-1">WhatsApp</div>
                      <div className="font-semibold text-gray-800 group-hover:text-amber-600 transition-colors">
                        Envie uma mensagem
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>

              <a href={`mailto:${mockData.school.email}`} className="block group">
                <Card className="hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-amber-200">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="bg-gradient-to-br from-red-400 to-pink-500 p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500 mb-1">E-mail</div>
                      <div className="font-semibold text-gray-800 group-hover:text-amber-600 transition-colors">
                        {mockData.school.email}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>

              <a href={mockData.school.mapsLink} target="_blank" rel="noopener noreferrer" className="block group">
                <Card className="hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-amber-200">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500 mb-1">Endereço</div>
                      <div className="font-semibold text-gray-800 group-hover:text-amber-600 transition-colors">
                        {mockData.school.address}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>
            </div>

            {/* Schedule Info */}
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-md mt-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-amber-400 to-orange-400 p-3 rounded-xl flex-shrink-0">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-2">Horário de Atendimento</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {mockData.school.schedule}<br />
                      <span className="text-amber-600 font-semibold">{mockData.school.visitPolicy}</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="shadow-xl border-2 border-amber-100">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Envie uma Mensagem</h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium mb-2 block">
                      Nome Completo *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Seu nome"
                      className="border-2 border-gray-200 focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                      E-mail *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="seu@email.com"
                      className="border-2 border-gray-200 focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium mb-2 block">
                      Telefone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(00) 00000-0000"
                      className="border-2 border-gray-200 focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-medium mb-2 block">
                      Mensagem *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Como podemos ajudar?"
                      rows={5}
                      className="border-2 border-gray-200 focus:border-amber-400 transition-colors resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Enviando...'
                    ) : (
                      <>
                        Enviar Mensagem
                        <Send className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Google Maps */}
        <div className="mt-16 max-w-6xl mx-auto">
          <Card className="overflow-hidden shadow-xl border-2 border-amber-100">
            <div className="h-96 bg-gray-200 relative">
              <iframe
                title="Localização Recanto Abelhinha"
                src={`https://maps.google.com/maps?q=Rua+Borborema+189+Vila+Fortuna+Campinas+SP&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                className="w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};
